import type { OffersSchemas } from "api-types";
import dayjs, { type Dayjs } from "dayjs";

import { daysToMonths } from "./utils";

export type OfferSummary = {
  deploymentCurrency: string;
  collectionCurrency: string;
  collectionType: OffersSchemas.CollectionType;
  collectionFrequency: OffersSchemas.CollectionFrequency;
  collectionMode: OffersSchemas.CollectionMode;
  initialDeploymentDate: Dayjs;
  numberOfTranches: number;
  totalAdvanceInLowestDenomination: number;
  totalFeeInLowestDenomination: number;
  totalRemittanceInLowestDenomination: number;
  dailyCollectionFixedInLowestDenomination: number;
  dailyCollectionPercentage: number;
  estimatedTermInMonths: number;
  capType?: string;
  capAmountInLowestDenomination?: number;
};

export type AggregatedData = {
  startDate: string;
  advanceInLowestDenomination: number;
  feeInLowestDenomination: number;
  totalRemittanceInLowestDenomination: number;
  dailyCollectionFixedInLowestDenomination: number;
  dailyCollectionPercentage: number;
  collectionType: OffersSchemas.CollectionType;
};

const blendedAggregator = (acc: number, cur: number | null | undefined, idx: number): number => {
  if (idx === 0) {
    return cur ?? 0;
  } else {
    return acc;
  }
};

const stackedAggregator = (acc: number, cur: number | null | undefined): number => {
  if (!cur) {
    return acc;
  } else {
    return acc + cur;
  }
};

export const aggregateTrancheData = (
  tranches: OffersSchemas.OfferTrancheDisplaySchema[],
  collectionMode: OffersSchemas.CollectionMode
): AggregatedData => {
  /**
   * TODO: There could be a mix of fixed and percentage remittance type across tranches. This is not supported yet.
   * The backend will guard against these types being published.
   */
  /**
   * TODO: There could be a mix of deployment currencies across tranches. This is not supported yet.
   * The backend will guard against these types being published.
   */
  /**
   * TODO: There could be a difference in deployment and collection currency. This is not supported yet.
   * The backend will guard against these types being published.
   */
  const aggregator = collectionMode === "blended" ? blendedAggregator : stackedAggregator;
  const startDate = tranches[0].startDate;

  const aggregatedData: AggregatedData = {
    startDate: startDate,
    advanceInLowestDenomination: 0,
    feeInLowestDenomination: 0,
    totalRemittanceInLowestDenomination: 0,
    collectionType: tranches[0].collectionType,
    dailyCollectionFixedInLowestDenomination: 0,
    dailyCollectionPercentage: 0,
  };

  tranches.forEach((cur, idx) => {
    aggregatedData.advanceInLowestDenomination =
      aggregatedData.advanceInLowestDenomination + cur.advanceInLowestDenomination;
    aggregatedData.feeInLowestDenomination = aggregatedData.feeInLowestDenomination + cur.feeInLowestDenomination;
    aggregatedData.totalRemittanceInLowestDenomination =
      aggregatedData.totalRemittanceInLowestDenomination +
      cur.advanceInLowestDenomination +
      cur.feeInLowestDenomination;
    aggregatedData.dailyCollectionFixedInLowestDenomination = aggregator(
      aggregatedData.dailyCollectionFixedInLowestDenomination,
      cur.dailyCollectionFixedInLowestDenomination,
      idx
    );
    aggregatedData.dailyCollectionPercentage = aggregator(
      aggregatedData.dailyCollectionPercentage,
      cur.dailyCollectionPercentage,
      idx
    );
  });

  return aggregatedData;
};

export const summariseOfferData = (offer: OffersSchemas.OfferDisplaySchema): OfferSummary => {
  const { tranches } = offer;
  const aggregatedData = aggregateTrancheData(tranches, offer.collectionMode);

  return {
    deploymentCurrency: tranches[0].deploymentCurrency,
    collectionCurrency: offer.collectionCurrency,
    collectionType: aggregatedData.collectionType,
    collectionFrequency: offer.collectionFrequency,
    collectionMode: offer.collectionMode,
    initialDeploymentDate: dayjs(tranches[0].startDate),
    numberOfTranches: tranches.length,
    totalAdvanceInLowestDenomination: aggregatedData.advanceInLowestDenomination,
    totalFeeInLowestDenomination: aggregatedData.feeInLowestDenomination,
    totalRemittanceInLowestDenomination: aggregatedData.totalRemittanceInLowestDenomination,
    dailyCollectionFixedInLowestDenomination: aggregatedData.dailyCollectionFixedInLowestDenomination,
    dailyCollectionPercentage: aggregatedData.dailyCollectionPercentage,
    estimatedTermInMonths: daysToMonths(offer.estimatedDurationDays),
    capType: offer.capType ?? undefined,
    capAmountInLowestDenomination:
      offer.caps.length > 0 ? offer.caps[0].maxCollectionAmountInLowestDenomination : undefined,
  };
};
